import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useMemo, useState } from "react";
import { Control, useController, useWatch } from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { useDebounce } from "hooks";

import { useAppSelector } from "app/hooks";
import { error } from "app/themes/colors";
import AssignedTagsList from "components/assigned-tags";
import PatientAlertChip from "components/patient-search/patient-alert-chip.component";
import PatientSearchOption from "components/patient-search/patient-search-option.component";
import {
  SHOW_INACTIVE_PATIENTS_SEARCH,
  SHOW_PATIENT_SEARCH_ALERT_COUNT
} from "constants/feature-flags";
import { CRM_SEGMENT_PROSPECT } from "constants/patients";
import { TagTypes } from "constants/tags";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import PatientForm from "features/patients/patient-form";
import { PatientFormValues } from "features/patients/patient-form/patient-form.component";
import { useGetPatientAssignedTagsQuery } from "services/assigned-tags";
import {
  DEFAULT_PATIENTS_QUERY,
  PATIENT_SEARCH_QUERY,
  PATIENT_SEARCH_QUERY_WITH_ALERT_COUNT,
  useLazyGetPatientQuery,
  useQueryPatientsQuery
} from "services/patients";
import type { Patient } from "services/types/patients.type";

import { AppointmentFormValues } from "../../appointment-form.component";
import {
  createPatientFromInput,
  createPatientFromWebPatientData
} from "./utils";
import WebPatientDetails from "./web-patient-details.component";

export type PatientOption = Patient & { isNew: boolean };

export const NEW_PATIENT_OPTION = {
  id: -1,
  first_name: "",
  middle_name: "",
  last_name: "",
  preferred_name: "",
  preferred_communication_method: "",
  phone_type: "",
  title: null,
  suffix: "",
  birthdate: null,
  phone: "",
  email: "",
  ssn: null,
  marital_status: null,
  sex: null,
  isNew: true,
  age_years: null,
  totalBalance: 0,
  crm_segment: CRM_SEGMENT_PROSPECT
};

type PatientFieldProps = {
  control: Control<AppointmentFormValues, object>;
  disabled?: boolean;
  showPatientTags?: boolean;
  appointmentId?: number | null;
  onOptionSelected?: (
    patient: Patient | null,
    options?: { isNewlyCreated?: boolean }
  ) => void;
};

const getOptionLabel = (option: Patient) => {
  const fullName = [option.first_name, option.middle_name, option.last_name]
    .filter((namePart) => Boolean(namePart))
    .join(" ");
  if (option.id === -1) {
    const addValue = fullName || option.phone || option.email;
    return `Add "${addValue}" as new Patient`;
  }
  return fullName;
};

export default function PatientField({
  control,
  disabled,
  showPatientTags,
  appointmentId,
  onOptionSelected
}: PatientFieldProps) {
  const flags = useFlags();
  const showInactivePatients = flags[SHOW_INACTIVE_PATIENTS_SEARCH];
  const showPatientSearchAlertCount =
    flags[SHOW_PATIENT_SEARCH_ALERT_COUNT] ?? false;
  const patientSearchQuery = showPatientSearchAlertCount
    ? PATIENT_SEARCH_QUERY_WITH_ALERT_COUNT
    : PATIENT_SEARCH_QUERY;

  const { shadows } = useTheme();

  const [openPatientForm, setOpenPatientForm] = useState<boolean>(false);
  const [patientSearch, setPatientSearch] = useState<string>("");
  const [newPatient, setNewPatient] = useState<Partial<PatientFormValues>>({});
  const [patient, webPatientData] = useWatch({
    name: ["patient", "web_patient"],
    control
  });
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);

  const {
    field: { onChange, onBlur, value, ref },
    formState: { errors }
  } = useController({
    name: "patient",
    control,
    rules: {
      required: "Patient is required"
    },
    defaultValue: undefined
  });
  const { value: dPatientSearch, isDebouncing } = useDebounce(
    patientSearch,
    200
  );

  const { data, isLoading } = useQueryPatientsQuery(
    {
      search:
        dPatientSearch && dPatientSearch.length >= 3
          ? dPatientSearch
          : undefined,
      baseFilter: {
        preferred_clinic: currentPractice?.limit_to_current_clinic
          ? currentClinic.id
          : undefined
      },
      is_active: showInactivePatients ? undefined : true,
      query: patientSearchQuery
    },
    {
      skip: !currentPractice?.id || !currentClinic?.id
    }
  );

  const [getPatientData] = useLazyGetPatientQuery();

  const [showPatientTagsTooltip, setShowPatientTagsTooltip] =
    React.useState(false);

  const { data: tags_for_existing_appointment } =
    useGetPatientAssignedTagsQuery(
      {
        object_id: value?.id
      },
      {
        skip: Boolean(
          !appointmentId || !value?.id || value?.assigned_tags?.length
        )
      }
    );

  const assigned_tags = useMemo(() => {
    return value?.assigned_tags?.length
      ? value.assigned_tags
      : tags_for_existing_appointment || value?.assigned_tags;
  }, [tags_for_existing_appointment, value?.assigned_tags]);

  const tagsAvailable = showPatientTags && Boolean(assigned_tags?.length);

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (tagsAvailable) {
        setShowPatientTagsTooltip(true);
      }
    },
    [tagsAvailable]
  );

  const handlePopoverClose = useCallback(() => {
    setShowPatientTagsTooltip(false);
  }, []);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <SearchIcon sx={{ mt: 0.5, mr: 1.5 }} color="action" />
        <Autocomplete
          disabled={disabled}
          fullWidth
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          popupIcon={<ExpandMoreOutlinedIcon />}
          inputValue={patientSearch}
          getOptionLabel={(option) => getOptionLabel(option)}
          onInputChange={(_event, searchValue) => setPatientSearch(searchValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <Box>
              <Stack
                flexDirection="row"
                gap={2}
                alignItems="end"
                position="relative"
              >
                <TextField
                  {...params}
                  required
                  label="Patient Search"
                  error={Boolean(errors?.patient?.message)}
                  sx={{
                    "& .MuiAutocomplete-input": {
                      height: tagsAvailable ? "48px" : undefined,
                      marginTop: tagsAvailable ? "-17px" : undefined
                    },
                    textDecoration:
                      showInactivePatients && patient && !patient.is_active
                        ? "line-through"
                        : undefined
                  }}
                />
                {tagsAvailable && (
                  <Box position="absolute" zIndex={1} top="45px" left="12px">
                    <AssignedTagsList
                      tagType={TagTypes.PATIENT}
                      tags={assigned_tags}
                      dotsSize="10px"
                      showDotsOnly
                      sx={{ flexDirection: "column" }}
                    />
                  </Box>
                )}
                {showPatientTagsTooltip && (
                  <Box
                    sx={{
                      border: `solid 1px ${grey[300]}`,
                      background: grey[100],
                      boxShadow: shadows[2],
                      position: "absolute",
                      pointerEvents: "none",
                      bottom: "-72px",
                      zIndex: 2,
                      p: 1,
                      borderRadius: "4px",
                      maxWidth: "562px"
                    }}
                  >
                    <Stack rowGap={0.5}>
                      <Typography variant="body1">
                        Patient&nbsp;Tags:
                      </Typography>
                      <AssignedTagsList
                        tagType={TagTypes.PATIENT}
                        tags={assigned_tags}
                        sx={{ flexDirection: "column" }}
                      />
                    </Stack>
                  </Box>
                )}

                <Button
                  disabled={disabled}
                  variant="contained"
                  onClick={() => {
                    setNewPatient({});
                    setOpenPatientForm(true);
                  }}
                  size="small"
                >
                  <AddIcon fontSize="small" />
                  Quick&nbsp;Add
                </Button>
              </Stack>
              {errors.patient?.message && (
                <FormHelperText sx={{ mt: 0.5, color: error[600] }}>
                  {errors.patient.message as string}
                </FormHelperText>
              )}
              {showPatientSearchAlertCount &&
                value &&
                value.id !== -1 &&
                (value.active_alert_count ?? 0) > 0 && (
                  <Box
                    sx={{ mt: 1.5 }}
                    onMouseEnter={(event) => {
                      event.stopPropagation();
                      handlePopoverClose();
                    }}
                  >
                    <PatientAlertChip
                      patientId={value.id}
                      count={value.active_alert_count ?? 0}
                    />
                  </Box>
                )}
            </Box>
          )}
          renderOption={(props, option: Patient) => (
            <PatientSearchOption
              key={option.id}
              renderProps={props}
              option={option}
              allowAddNewPatient
              showPatientTags={showPatientTags}
              showPatientAlertCount={showPatientSearchAlertCount}
            />
          )}
          value={value}
          ref={ref}
          onBlur={() => {
            if (!openPatientForm) {
              onBlur();
            }
          }}
          onChange={(_event, value) => {
            if (value && value.id === -1) {
              const { first_name, middle_name, last_name, phone, email } =
                value;
              setNewPatient({
                first_name,
                middle_name,
                last_name,
                phone,
                email
              });
              setOpenPatientForm(true);
            } else {
              onChange(value);
              if (value) {
                getPatientData({
                  patientId: value.id,
                  query: DEFAULT_PATIENTS_QUERY
                }).then(({ data: patientData }) => {
                  if (patientData) {
                    onChange({
                      ...patientData,
                      active_alert_count: value.active_alert_count,
                      isNew: false
                    });
                    onOptionSelected?.(patientData);
                  }
                  onBlur();
                });
              } else {
                onOptionSelected?.(value);
                onBlur();
              }
            }
          }}
          options={data?.map((patient) => ({ ...patient, isNew: false })) || []}
          loading={isLoading || isDebouncing}
          filterOptions={(options, params) => {
            const filtered = options.map((option) => option);
            if (params.inputValue !== "") {
              const newPatient = createPatientFromInput(params.inputValue);
              filtered.push(newPatient);
            }
            if (webPatientData) {
              const webPatient =
                createPatientFromWebPatientData(webPatientData);
              filtered.push(webPatient);
            }
            return filtered;
          }}
        />
      </Box>
      {webPatientData && !patient && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <WebPatientDetails
            firstName={webPatientData.first_name}
            lastName={webPatientData.last_name}
            email={webPatientData.email || ""}
            birthdate={
              webPatientData.birthdate ? new Date(webPatientData.birthdate) : ""
            }
            phone={webPatientData.phone}
          />
          <Button
            disabled={disabled}
            variant="outlined"
            sx={{ flex: "0 0 30%", ml: 2.5 }}
            onClick={() => {
              setNewPatient({
                first_name: webPatientData.first_name?.trim() || "",
                last_name: webPatientData.last_name?.trim() || "",
                email: webPatientData.email?.trim() || "",
                phone: webPatientData.phone?.trim() || "",
                birthdate: webPatientData.birthdate
                  ? new Date(webPatientData.birthdate)
                  : null
              });
              setOpenPatientForm(true);
            }}
          >
            Add{" "}
            {[webPatientData.first_name, webPatientData.last_name].join(" ")}
          </Button>
        </Box>
      )}
      <PatientForm
        isOpen={openPatientForm}
        onCancel={() => setOpenPatientForm(false)}
        onSave={async (patient) => {
          const { data: patientData } = await getPatientData({
            patientId: patient.id,
            query: DEFAULT_PATIENTS_QUERY
          });

          onChange({ ...patientData, isNew: false });
          patientData &&
            onOptionSelected?.(patientData, { isNewlyCreated: true });

          onBlur();
          setOpenPatientForm(false);
        }}
        newPatient={newPatient}
      />
    </>
  );
}
